
"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/shadcn/button";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import { useHeroSlider, type HeroSliderItem } from "../hooks/useHeroSlider";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePrefersLightMedia } from "@/hooks/usePrefersLightMedia";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function getContentAlignment(position?: string | null) {
  const normalized = (position ?? "center")
    .toLowerCase()
    .replaceAll("_", "-")
    .replace("middle", "center");

  let horizontal: "left" | "center" | "right" = "center";
  let vertical: "top" | "center" | "bottom" = "center";

  if (normalized.includes("left")) horizontal = "left";
  if (normalized.includes("right")) horizontal = "right";
  if (normalized === "left" || normalized === "right") vertical = "center";

  if (normalized.includes("top")) vertical = "top";
  if (normalized.includes("bottom")) vertical = "bottom";
  if (normalized === "top" || normalized === "bottom") horizontal = "center";

  if (normalized === "centered") {
    horizontal = "center";
    vertical = "center";
  }

  const horizontalClasses = {
    left: {
      wrapper: "items-start text-left",
      content: "items-start text-left",
      button: "self-start",
      contentWidth: "max-w-[92%] md:max-w-[80%]",
    },
    center: {
      wrapper: "items-center text-center",
      content: "items-center text-center",
      button: "self-center",
      contentWidth: "max-w-[92%] md:max-w-[80%]",
    },
    right: {
      wrapper: "items-end text-right",
      content: "items-end text-right",
      button: "self-end",
      contentWidth: "max-w-[92%] md:max-w-[80%]",
    },
  };

  const verticalWrapperClass = {
    top: "justify-start pt-5",
    center: "justify-center",
    bottom: "justify-end pb-5",
  }[vertical];

  return {
    wrapper: `${verticalWrapperClass} ${horizontalClasses[horizontal].wrapper}`,
    content: horizontalClasses[horizontal].content,
    button: horizontalClasses[horizontal].button,
    contentWidth: horizontalClasses[horizontal].contentWidth,
  };
}

function getSlidePositionValue(slide: {
  content_position?: string | null;
  content_position_layout?: string | null;
  content_position_location?: string | null;
}) {
  const layout = slide.content_position_layout?.toLowerCase().replaceAll("_", "-");
  const location = slide.content_position_location?.toLowerCase().replaceAll("_", "-");

  if (layout && location) {
    if (layout === "center") return location;
    if (location === "center") return layout;
    return `${location}-${layout}`;
  }

  return layout || location || slide.content_position || "center";
}

/**
 * Picks which hero video file to load.
 *
 * `video` is always the master encode; `video_mobile` is the optional lighter
 * encode the CMS can provide for small screens and slow connections. When the
 * CMS does not send `video_mobile` yet we keep the master file, so this is safe
 * today and starts working as soon as the field appears in the API.
 *
 * The pick happens in JS instead of `<source media="...">` because browsers do
 * not consistently honour the `media` attribute on <video> sources, and screen
 * size is only half of the story — slow connections matter too (see
 * `usePrefersLightMedia`).
 */
function pickHeroVideo(slide: HeroSliderItem, useLighterVariant: boolean): string | null {
  const master = slide.video ?? null;
  if (!useLighterVariant) return master;
  return slide.video_mobile ?? master;
}

type HeroVideoProps = {
  src: string;
  type?: string | null;
  poster?: string;
  active: boolean;
};

/**
 * Hero background video.
 *
 * CMS hero videos (MinIO) can be tens of MB, so the network is kept strictly
 * on the visible slide:
 *  - only the active slide uses preload="auto" + autoplay; every other slide
 *    stays on preload="none" so off-screen videos don't compete for bandwidth;
 *  - leaving a slide pauses its video, which also stops the download;
 *  - the poster layer behind it stays visible until the first decoded frame
 *    arrives (onLoadedData), so the hero is never blank/black while buffering;
 *  - if the file cannot be loaded/decoded the <video> is dropped and the
 *    poster layer simply remains.
 */
function HeroVideo({ src, type, poster, active }: HeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (active) {
      // play() also forces the load for slides parked on preload="none"
      void el.play().catch(() => {
        /* autoplay blocked — the poster layer keeps showing */
      });
    } else {
      el.pause();
    }
  }, [active]);

  if (failed) return null;

  // Some CMS entries ship a non-video mime type (or none at all); default to
  // video/mp4 so the browser doesn't reject the <source> entry.
  const mime = type?.startsWith("video/") ? type : "video/mp4";

  return (
    <video
      ref={videoRef}
      autoPlay={active}
      muted
      loop
      playsInline
      preload={active ? "auto" : "none"}
      poster={poster}
      onLoadedData={() => setReady(true)}
      onError={() => setFailed(true)}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
        ready ? "opacity-100" : "opacity-0"
      }`}
    >
      <source src={src} type={mime} />
    </video>
  );
}

export default function Carousel() {
  const { data, isLoading } = useHeroSlider();
  // realIndex of the slide the visitor is actually looking at (loop-safe).
  // Only this slide is allowed to download/play its hero video.
  const [activeIndex, setActiveIndex] = useState(0);

  // Which encode fits this visitor: small screens / touch devices / Data Saver
  // get the lighter variant when the CMS provides one. Both sources are
  // deterministic (viewport + explicit user preference), so the hero never
  // swaps files mid-flight and downloads twice.
  const wantsLighterVideo = useMediaQuery("(max-width: 768px), (pointer: coarse)");
  const prefersLightMedia = usePrefersLightMedia();
  const useLighterVariant = wantsLighterVideo || prefersLightMedia;

  if (isLoading) {
    return <div className="flex h-[70vh] min-h-[420px] w-full items-center justify-center md:h-202.5">Loading...</div>;
  }

  return (
    <div className="h-[70vh] min-h-[420px] w-full md:h-202.5">
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        navigation
        autoplay={{ delay: 10000, disableOnInteraction: false }}
        loop={true}
        onSwiper={(swiper) => setActiveIndex(swiper.realIndex)}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className=""
      >
        <AnimatePresence mode="wait">
          {data?.data.map((slide, index) => {
            const alignment = getContentAlignment(getSlidePositionValue(slide));
            const isActive = index === activeIndex;
            const videoSrc = pickHeroVideo(slide, useLighterVariant);
            const hasTitle = Boolean(slide.title?.trim());
            const hasDescription = Boolean(slide.desc?.trim());
            const hasCta = Boolean(slide.cta_url?.trim() && slide.cta_text?.trim());

            return (
            <SwiperSlide key={slide.id}>
              <div className={`relative flex h-[70vh] min-h-[420px] flex-col overflow-hidden px-6 text-2xl font-bold text-white sm:px-8 md:h-202.5 md:px-16 lg:px-24 ${alignment.wrapper}`}>
                {videoSrc ? (
                  <>
                    {/* Poster/fallback layer: sits behind the video so the hero
                        never shows a black gap while it buffers (or if it fails
                        to decode). CMS image when available, brand gradient
                        otherwise. */}
                    {slide.image ? (
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${slide.image})` }}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-linear-to-br from-primary-700 to-primary-800" />
                    )}
                    <HeroVideo
                      key={videoSrc}
                      src={videoSrc}
                      type={slide.video_type}
                      poster={slide.image ?? undefined}
                      active={isActive}
                    />
                    <div className="absolute inset-0 bg-black/30" />
                  </>
                ) : (
                  <div
                    className="hero-bg-zoom absolute inset-0 bg-no-repeat bg-cover"
                    style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${slide.image})` }}
                  />
                )}
                {(hasTitle || hasDescription || hasCta) && (
                  <motion.div
                    className={`relative z-10 flex w-full flex-col gap-4 md:gap-6 ${alignment.content} ${alignment.contentWidth ?? "max-w-[80%]"}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    key={slide.id}
                  >
                    {hasTitle && (
                      <motion.h1
                        className="font-inter font-bold capitalize leading-tight"
                        style={{ fontSize: `clamp(2rem, 8vw, ${slide.title_font_size ?? 96}px)` }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        {slide.title}
                      </motion.h1>
                    )}
                    {hasDescription && (
                      <motion.span
                        className="font-medium"
                        style={{ fontSize: `clamp(0.875rem, 3.5vw, ${slide.desc_font_size ?? 16}px)` }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                      >
                        {slide.desc}
                      </motion.span>
                    )}
                    {hasCta && (
                      <motion.div
                        className={alignment.button}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                      >
                        <a href={slide.cta_url!}>
                          <Button
                            variant={"default"}
                            className="
                              cursor-pointer
                              mt-8
                              w-fit 
                              bg-white 
                              text-primary-800! 
                              text-[16px] 
                              font-medium! 
                              hover:border-2
                              hover:border-primary-800
                              hover:bg-primary-800
                              hover:text-white!
                              hover:py-2.5"
                          >
                            {slide.cta_text}
                          </Button>
                        </a>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>
            </SwiperSlide>
          );})}
        </AnimatePresence>
      </Swiper>
    </div>
  );
}

